import React, { useEffect, useState } from "react";
import axios from "axios";
import Swal from "sweetalert2";
import moment from "moment";
import { Button, Input, Label, Table } from "reactstrap";
import ExportJsonExcel from "js-export-excel";
import { URL_FINANZAS, auth, fmt, fmtCorto } from "./finanzasUtils";

// Dos series por gráfica: la paleta pasa la validación de daltonismo y contraste
const SERIE_A = "#2563EB";
const SERIE_B = "#EA580C";

const MESES = ["ene", "feb", "mar", "abr", "may", "jun", "jul", "ago", "sep", "oct", "nov", "dic"];
const nombreMes = (m) => `${MESES[Number(m.slice(5, 7)) - 1]} ${m.slice(2, 4)}`;

const techo = (v) => {
  if (!(v > 0)) return 1;
  const base = 10 ** Math.floor(Math.log10(v));
  return [1, 2, 2.5, 5, 10].map((x) => x * base).find((x) => x >= v);
};

const ANCHO = 720;
const ALTO = 260;
const MARGEN = { top: 12, right: 8, bottom: 26, left: 52 };

// Barras agrupadas por mes, una sola escala
function BarrasMes({ titulo, datos, serieA, serieB }) {
  const [activo, setActivo] = useState(null);
  const max = techo(Math.max(0, ...datos.map((d) => Math.max(d[serieA.campo], d[serieB.campo]))));
  const altoPlot = ALTO - MARGEN.top - MARGEN.bottom;
  const anchoPlot = ANCHO - MARGEN.left - MARGEN.right;
  const banda = anchoPlot / Math.max(datos.length, 1);
  const anchoBarra = Math.min(22, (banda - 10) / 2);
  const y = (v) => MARGEN.top + altoPlot - (v / max) * altoPlot;
  const lineas = [0, 0.25, 0.5, 0.75, 1].map((p) => p * max);
  const cada = Math.ceil(datos.length / 12);

  const barra = (d, serie, x) => {
    const alto = Math.max(y(0) - y(d[serie.campo]), d[serie.campo] > 0 ? 1 : 0);
    const r = Math.min(4, alto, anchoBarra / 2);
    const top = y(0) - alto;
    return <path d={`M${x},${y(0)}V${top + r}Q${x},${top} ${x + r},${top}H${x + anchoBarra - r}Q${x + anchoBarra},${top} ${x + anchoBarra},${top + r}V${y(0)}Z`} fill={serie.color} />;
  };

  return (
    <div className="fz-grafica">
      <div className="fz-grafica-head">
        <h5>{titulo}</h5>
        <div className="fz-leyenda">
          {[serieA, serieB].map((s) => <span key={s.campo}><i style={{ background: s.color }} />{s.nombre}</span>)}
        </div>
      </div>
      {datos.length === 0 ? (
        <div className="td-muted">Sin datos en el periodo.</div>
      ) : (
        <div className="fz-plot" onMouseLeave={() => setActivo(null)}>
          <svg viewBox={`0 0 ${ANCHO} ${ALTO}`} role="img" aria-label={titulo}>
            {lineas.map((v) => (
              <g key={v}>
                <line x1={MARGEN.left} x2={ANCHO - MARGEN.right} y1={y(v)} y2={y(v)} className="fz-grid" />
                <text x={MARGEN.left - 8} y={y(v) + 3} textAnchor="end" className="fz-eje">{fmtCorto(v)}</text>
              </g>
            ))}
            {datos.map((d, i) => {
              const x0 = MARGEN.left + banda * i;
              const inicioBarras = x0 + (banda - anchoBarra * 2 - 2) / 2;
              return (
                <g key={d.mes} onMouseEnter={() => setActivo(i)}>
                  <rect x={x0} y={MARGEN.top} width={banda} height={altoPlot + MARGEN.bottom} className={activo === i ? "fz-banda on" : "fz-banda"} />
                  {barra(d, serieA, inicioBarras)}
                  {barra(d, serieB, inicioBarras + anchoBarra + 2)}
                  {i % cada === 0 ? <text x={x0 + banda / 2} y={ALTO - 8} textAnchor="middle" className="fz-eje">{nombreMes(d.mes)}</text> : null}
                </g>
              );
            })}
          </svg>
          {activo !== null ? (
            <div className="fz-tooltip" style={{ left: `${((MARGEN.left + banda * (activo + 0.5)) / ANCHO) * 100}%` }}>
              <strong>{nombreMes(datos[activo].mes)}</strong>
              <div>{serieA.nombre}: {fmt(datos[activo][serieA.campo])}</div>
              <div>{serieB.nombre}: {fmt(datos[activo][serieB.campo])}</div>
            </div>
          ) : null}
        </div>
      )}
    </div>
  );
}

function BarrasTop({ titulo, datos }) {
  const max = Math.max(1, ...datos.map((d) => d.saldo));
  return (
    <div className="fz-grafica">
      <div className="fz-grafica-head"><h5>{titulo}</h5></div>
      {datos.length === 0 ? <div className="td-muted">Sin saldos en el periodo.</div> : datos.map((d) => (
        <div key={d.nombre} className="fz-top" title={`${d.nombre}: ${fmt(d.saldo)}`}>
          <span className="fz-top-nombre">{d.nombre}</span>
          <span className="fz-top-pista"><i style={{ width: `${(d.saldo / max) * 100}%`, background: SERIE_A }} /></span>
          <span className="fz-top-valor">{fmtCorto(d.saldo)}</span>
        </div>
      ))}
    </div>
  );
}

// Reportes y gráficas del periodo: el dinero sale de los mismos registros que el control por contenedor
export default function TabReportes({ refresco }) {
  const [inicio, setInicio] = useState(moment().startOf("year").format("YYYY-MM-DD"));
  const [fin, setFin] = useState(moment().format("YYYY-MM-DD"));
  const [datos, setDatos] = useState(null);

  useEffect(() => {
    setDatos(null);
    axios
      .get(`${URL_FINANZAS}Reporte/${inicio}/${fin}`, auth())
      .then(({ data }) => setDatos(data))
      .catch((err) => Swal.fire({ icon: "error", title: "No se pudo armar el reporte", text: err?.response?.data?.message || err.message }));
  }, [inicio, fin, refresco]);

  const excel = () => {
    const hoja = (sheetName, sheetData) => {
      const cols = Object.keys(sheetData[0] || { Sin: "" });
      return { sheetData, sheetName, sheetFilter: cols, sheetHeader: cols };
    };
    new ExportJsonExcel({
      fileName: `ReporteFinanzas-${inicio}-${fin}`,
      datas: [
        hoja("Por mes", datos.meses.map((m) => ({ Mes: m.mes, Cargado: m.cargado, Abonado: m.abonado, Gastos: m.gastos, Pagado: m.pagado }))),
        hoja("Desglose", datos.desglose.map((d) => ({ Concepto: d.nombre, Total: d.total, Saldo: d.saldo }))),
        hoja("Saldo clientes", datos.topClientes.map((d) => ({ Cliente: d.nombre, Saldo: d.saldo }))),
        hoja("Saldo brokers", datos.topBrokers.map((d) => ({ Broker: d.nombre, Saldo: d.saldo }))),
      ],
    }).saveExcel();
  };

  const k = datos ? datos.kpis : {};
  return (
    <>
      <div className="filter-bar">
        <div>
          <Label>Fecha inicio</Label>
          <Input bsSize="sm" type="date" value={inicio} onChange={(e) => setInicio(e.target.value)} />
        </div>
        <div>
          <Label>Fecha fin</Label>
          <Input bsSize="sm" type="date" value={fin} onChange={(e) => setFin(e.target.value)} />
        </div>
        <div className="fz-excel">
          <Button size="sm" onClick={excel} disabled={!datos}><i className="far fa-file-excel" /> Excel</Button>
        </div>
      </div>

      {!datos ? (
        <div className="rc-state"><i className="fas fa-spinner fa-spin" /><div>Armando el reporte…</div></div>
      ) : (
        <>
          <div className="fz-kpis">
            {[
              ["Cargado a clientes", k.cargado], ["Abonado", k.abonado], ["Por cobrar", k.porCobrar],
              ["Gastos", k.gastos], ["Pagado a brokers", k.pagado], ["Por pagar", k.porPagar],
            ].map(([label, valor]) => (
              <div key={label} className="fz-kpi" title={fmt(valor)}>
                <span>{label}</span>
                <strong>{fmtCorto(valor)}</strong>
              </div>
            ))}
          </div>

          <div className="fz-graficas">
            <BarrasMes
              titulo="Cobranza por mes"
              datos={datos.meses}
              serieA={{ campo: "cargado", nombre: "Cargado", color: SERIE_A }}
              serieB={{ campo: "abonado", nombre: "Abonado", color: SERIE_B }}
            />
            <BarrasMes
              titulo="Costos por mes"
              datos={datos.meses}
              serieA={{ campo: "gastos", nombre: "Gastos", color: SERIE_A }}
              serieB={{ campo: "pagado", nombre: "Pagado", color: SERIE_B }}
            />
            <BarrasTop titulo="Clientes con mayor saldo" datos={datos.topClientes} />
            <BarrasTop titulo="Brokers con mayor saldo" datos={datos.topBrokers} />
          </div>

          <div className="fz-tablas">
            <div>
              <h5 className="fz-titulo">Desglose de lo cargado</h5>
              <Table size="sm">
                <thead><tr><th>Concepto</th><th className="td-num">Total</th><th className="td-num">Saldo</th></tr></thead>
                <tbody>
                  {datos.desglose.map((d) => (
                    <tr key={d.nombre}>
                      <td>{d.nombre}</td>
                      <td className="td-num td-money">{fmt(d.total)}</td>
                      <td className="td-num td-money">{fmt(d.saldo)}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
            <div>
              <h5 className="fz-titulo">Por mes</h5>
              <Table size="sm">
                <thead><tr><th>Mes</th><th className="td-num">Cargado</th><th className="td-num">Abonado</th><th className="td-num">Gastos</th><th className="td-num">Pagado</th></tr></thead>
                <tbody>
                  {datos.meses.map((m) => (
                    <tr key={m.mes}>
                      <td>{nombreMes(m.mes)}</td>
                      <td className="td-num td-money">{fmt(m.cargado)}</td>
                      <td className="td-num td-money">{fmt(m.abonado)}</td>
                      <td className="td-num td-money">{fmt(m.gastos)}</td>
                      <td className="td-num td-money">{fmt(m.pagado)}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          </div>
        </>
      )}
    </>
  );
}
